import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { ArrowLeftIcon, ExternalLinkIcon, MailIcon, PrinterIcon } from 'lucide-react'
import { AsortiGrid } from '@app/components/asorti-grid'
import { CariLink, CariOnly, StockLink } from '@app/components/entity-link'
import { ProductImage } from '@app/components/product-image'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardHeader, CardTitle } from '@app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { ApiError } from '@app/lib/api'
import { cn } from '@app/lib/cn'
import { PrintPortal, printedOn, usePrintJob } from '@app/lib/print-portal'
import { purchasingApi, purchasingQueryKeys } from '@app/features/satinalma/api'
import type {
  PurchaseDetailResponse,
  PurchaseLine,
  PurchaseSizeCell,
} from '@app/features/satinalma/api'
import {
  formatAmount,
  formatAmountOrDash,
  formatDate,
  formatDateTime,
  formatPrice,
  formatQty,
  percentText,
  unitLabel,
} from '@app/features/satinalma/format'
import {
  DeliveryDateCell,
  EmptyState,
  ErrorState,
  Field,
  QtyTriple,
  ReceiptBadge,
  ReceiptBar,
  StatusBadge,
  TableSkeleton,
  toSizeCells,
} from '@app/features/satinalma/ui'
import '@app/styles/print-docs.css'

/**
 * Satınalma detail — one purchase order: supplier, dates, material lines and
 * what has actually arrived per line.
 *
 * The received quantities come from the Si_GCHar ledger with the purchase-order
 * inversion applied (Cikan = ordered, Giren = received). The size strip under
 * each line is real data, not decoration: soles and heels are ordered per shoe
 * size, and 696 of the 934 purchase lines span more than one size.
 *
 * This is the CANONICAL view of a `SipTip = 'A'` order and the only one: there
 * is deliberately no "open in the orders view" escape hatch here. The sales
 * order screen reads the same ledger uninverted, so it would print every
 * quantity on a purchase order backwards — offering the way there would be
 * offering a wrong answer. The bridge runs one way only: /siparisler/<n> for a
 * purchase order redirects HERE (see `screens/siparisler/order-detail.tsx`).
 */
export function PurchaseDetailScreen() {
  const { sipNo } = useParams({ from: '/_korumali/satinalma/$sipNo' })

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: purchasingQueryKeys.detail(sipNo),
    queryFn: () => purchasingApi.detail(sipNo),
  })

  /*
   * 404 here means one of two things, and the answer to both is the list: the
   * number is not a purchase order at all, or it is a SALES order (the endpoint
   * refuses those on purpose — see `worker/api/satinalma.ts`). Retrying cannot
   * change either.
   */
  const notFound = error instanceof ApiError && error.status === 404

  return (
    <>
      <div className="mb-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/satinalma">
            <ArrowLeftIcon aria-hidden />
            Satınalma
          </Link>
        </Button>
      </div>

      {isPending ? (
        <DetailSkeleton />
      ) : isError ? (
        <Card>
          {notFound ? (
            <ErrorState
              title="Alış siparişi bulunamadı"
              message={`${sipNo} numaralı kayıt bir alış siparişi değil ya da Korgun'da yok. Listeden doğru siparişi seçebilirsiniz.`}
              action={
                <Button asChild variant="primary">
                  <Link to="/satinalma">Satınalma listesine dön</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState
              message={
                error instanceof Error ? error.message : 'Satınalma siparişi getirilemedi.'
              }
              onRetry={() => void refetch()}
            />
          )}
        </Card>
      ) : (
        <DetailContent data={data} />
      )}
    </>
  )
}

function DetailContent({ data }: { data: PurchaseDetailResponse }) {
  const { header, summary, currencyTotals, lines } = data
  const pricedTotals = currencyTotals.filter((t) => t.netAmount !== 0)

  /*
   * Nothing to fetch — the document is built from the response already on
   * screen. It still goes through the print job rather than a bare
   * `window.print()`: the snapshot taken on click is what keeps a background
   * refetch from rewriting the page while the dialog is open, and the job owns
   * the body class and the `afterprint` reset either way.
   */
  const print = usePrintJob<PurchaseDetailResponse>({
    bodyClass: PRINT_DOCUMENT_CLASS,
    resetKey: String(header.sipNo),
    load: async () => data,
    canPrint: (snapshot) => snapshot.lines.length > 0,
  })

  return (
    <div className="flex flex-col gap-3">
      {/* ── Header card ──────────────────────────────────────────────────── */}
      <Card>
        <div className="flex flex-col gap-4 px-3.5 py-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="eyebrow">
              Alış siparişi <span className="font-mono">{header.sipNo}</span>
              {header.documentNo ? (
                <>
                  {' · Belge '}
                  <span className="font-mono">{header.documentNo}</span>
                </>
              ) : null}
            </p>
            <h2 className="mt-0.5 font-display text-2xl font-semibold tracking-tight text-ink">
              <CariLink
                ckod={header.supplierKod}
                title={`${header.supplierName} cari kartını aç`}
                className="text-ink hover:text-kordovan hover:no-underline"
                plainClassName="text-ink"
              >
                {header.supplierName}
              </CariLink>
            </h2>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-data text-ink-muted">
              <CariLink ckod={header.supplierKod} className="font-mono" />
              {header.supplierTitle && header.supplierTitle !== header.supplierName ? (
                <span>· {header.supplierTitle}</span>
              ) : null}
              {header.supplierCity ? <span>· {header.supplierCity}</span> : null}
              {header.supplierPhone ? (
                <span className="font-mono">· {header.supplierPhone}</span>
              ) : null}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <StatusBadge status={header.status} />
              <ReceiptBadge status={summary.receiptStatus} overdue={summary.overdue} />
              {summary.overdue && summary.overdueDays !== null ? (
                <Badge tone="warn">Teslim {formatQty(summary.overdueDays)} gün geçti</Badge>
              ) : null}
              {header.supplierOverseas ? <Badge tone="info">Yurt dışı</Badge> : null}
              {summary.mixedCurrency ? <Badge tone="warn">Karışık para birimi</Badge> : null}
              {/* The supplier name above stays, as plain text — it says who the
                  order belongs to. This button says nothing but "cari", so an
                  account without the module simply does not get it. */}
              <CariOnly>
                <Button asChild variant="outline" size="sm" className="ml-1">
                  <Link to="/cari/$ckod" params={{ ckod: header.supplierKod }}>
                    <ExternalLinkIcon aria-hidden />
                    Cari kartını aç
                  </Link>
                </Button>
              </CariOnly>
            </div>

            <DocumentActions
              data={data}
              onPrint={print.run}
              printDisabled={lines.length === 0}
            />
          </div>

          {/* Goods receipt — the number the owners open this page for. */}
          <div className="shrink-0 rounded-sm border border-hairline bg-kagit px-3 py-2.5 lg:w-72">
            <div className="flex items-baseline justify-between gap-3">
              <span className="eyebrow">Mal kabul</span>
              <span
                className={cn(
                  'font-mono text-xl tnum font-semibold',
                  summary.receiptStatus === 'complete'
                    ? 'text-zeytin'
                    : summary.overdue
                      ? 'text-hardal'
                      : 'text-ink',
                )}
              >
                {percentText(summary.percent)}
              </span>
            </div>
            <ReceiptBar
              percent={summary.percent}
              status={summary.receiptStatus}
              overdue={summary.overdue}
              showLabel={false}
              className="mt-1.5"
            />
            <div className="mt-2.5">
              <QtyTriple
                ordered={summary.orderedQty}
                received={summary.receivedQty}
                kalan={summary.kalanQty}
              />
            </div>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-hairline px-3.5 py-3 sm:grid-cols-3 lg:grid-cols-6">
          <Field label="Sipariş tarihi" value={formatDate(header.orderDate)} mono />
          <Field
            label="Teslim tarihi"
            value={
              <DeliveryDateCell
                dateText={formatDate(header.deliveryDate)}
                overdueDays={summary.overdueDays}
              />
            }
          />
          <Field label="Depo" value={header.locationName ?? header.location ?? '—'} />
          <Field label="Siparişi açan" value={header.orderedBy ?? header.createdBy ?? '—'} />
          <Field label="Satır sayısı" value={formatQty(summary.lineCount)} mono />
          <Field label="Son güncelleme" value={formatDateTime(header.updatedAt)} mono />
        </dl>

        {header.notes || header.closingNote ? (
          <div className="border-t border-hairline px-3.5 py-3">
            {header.notes ? (
              <>
                <p className="eyebrow">Sipariş notu</p>
                <p className="mt-1 whitespace-pre-line text-data text-ink">{header.notes}</p>
              </>
            ) : null}
            {header.closingNote ? (
              <>
                <p className={cn('eyebrow', header.notes && 'mt-3')}>Kapanış notu</p>
                <p className="mt-1 whitespace-pre-line text-data text-ink">{header.closingNote}</p>
              </>
            ) : null}
          </div>
        ) : null}
      </Card>

      {/* ── Lines ────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>Sipariş satırları</CardTitle>
          <span className="font-mono text-micro tnum text-ink-muted">
            {formatQty(summary.lineCount)} satır
          </span>
        </CardHeader>

        {lines.length === 0 ? (
          <EmptyState
            title="Bu siparişte satır yok"
            description="Korgun'da başlık açılmış ama satır girilmemiş. Listeye dönüp başka bir kayda bakabilirsiniz."
            action={
              <Button asChild variant="primary">
                <Link to="/satinalma">Satınalma listesine dön</Link>
              </Button>
            }
          />
        ) : (
          <TableScroller>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 text-right">#</TableHead>
                  <TableHead className="min-w-56">Malzeme</TableHead>
                  <TableHead className="hidden min-w-28 lg:table-cell">Renk</TableHead>
                  <TableHead className="hidden min-w-40 xl:table-cell">Beden dağılımı</TableHead>
                  <TableHead className="w-24 text-right">Sipariş</TableHead>
                  <TableHead className="w-20 text-right">Gelen</TableHead>
                  <TableHead className="w-20 text-right">Kalan</TableHead>
                  <TableHead className="w-36">Mal kabul</TableHead>
                  <TableHead className="hidden w-24 text-right md:table-cell">Fiyat</TableHead>
                  <TableHead className="w-28 text-right">Tutar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <LineRow key={line.sipHarInx} line={line} />
                ))}
              </TableBody>
            </Table>
          </TableScroller>
        )}

        {/* ── Totals ─────────────────────────────────────────────────────── */}
        {pricedTotals.length > 0 ? (
          <div className="flex flex-col items-end gap-1 border-t border-hairline px-3.5 py-2.5">
            {pricedTotals.map((total) => {
              const mainCurrency = total.currency === summary.currency
              return (
                <div
                  key={total.currency}
                  className={cn(
                    'flex w-full max-w-sm items-baseline justify-between gap-4',
                    mainCurrency ? 'text-ink' : 'text-ink-muted',
                  )}
                >
                  <span className={cn('text-data', mainCurrency && 'font-medium')}>
                    {mainCurrency
                      ? `Sipariş toplamı (${total.currency})`
                      : `${total.currency} karşılığı`}
                  </span>
                  <span
                    className={cn(
                      'font-mono tnum',
                      mainCurrency ? 'text-[0.9375rem] font-semibold' : 'text-data',
                    )}
                  >
                    {formatAmount(total.netAmount, total.currency)}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="border-t border-hairline px-3.5 py-2.5 text-right text-micro text-ink-muted">
            Bu siparişte fiyat girilmemiş — 563 alış siparişinin yalnızca 115'inde tutar var.
          </div>
        )}
      </Card>

      {/* Portalled straight onto <body>, outside #root — see lib/print-portal.tsx
          and styles/print-docs.css. Invisible on screen either way. */}
      {print.status === 'ready' ? <PurchaseOrderDocument data={print.data} /> : null}
    </div>
  )
}

/* ── Document actions ─────────────────────────────────────────────────────── */

/**
 * "Yazdır" and "E-posta ile gönder", side by side because they are two halves
 * of one errand: print the order, then mail the supplier that it is coming.
 *
 * The mail button is disabled far more often than not — 411 of the 425 cari
 * cards carry no e-mail, and not one of the 39 accounts that appear on a
 * purchase order has one today — so the disabled state has to say WHY and
 * where to fix it, not just look greyed out.
 */
function DocumentActions({
  data,
  onPrint,
  printDisabled,
}: {
  data: PurchaseDetailResponse
  onPrint: () => void
  printDisabled: boolean
}) {
  const mail = supplierMail(data)

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <Button
        variant="outline"
        size="sm"
        onClick={onPrint}
        disabled={printDisabled}
        title={
          printDisabled
            ? 'Satırı olmayan sipariş yazdırılamaz'
            : 'Satınalma siparişini A4 belge olarak yazdır'
        }
      >
        <PrinterIcon aria-hidden />
        Yazdır
      </Button>

      {/* The title sits on the wrapper as well as the button: a disabled
          <button> swallows pointer events in every browser, so the tooltip
          would never appear on the element that needs to explain itself. */}
      <span title={mail.disabledReason ?? undefined}>
        {mail.href === null ? (
          <Button variant="outline" size="sm" disabled title={mail.disabledReason ?? undefined}>
            <MailIcon aria-hidden />
            E-posta ile gönder
          </Button>
        ) : (
          <Button
            asChild
            variant="outline"
            size="sm"
            title={`${data.header.supplierEmail} adresine sipariş özeti taslağı hazırlar — PDF'i kendiniz eklemelisiniz`}
          >
            <a href={mail.href}>
              <MailIcon aria-hidden />
              E-posta ile gönder
            </a>
          </Button>
        )}
      </span>
    </div>
  )
}

/* ── Supplier mailto ──────────────────────────────────────────────────────── */

/**
 * Shape check on `Cari_Kart.eMail`. Korgun validates nothing on that column —
 * one of the 14 stored values is a web address ("www.springfootwear.com") —
 * and `mailto:www.springfootwear.com` opens a mail client with a broken
 * recipient. Same expression the account seeder uses.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface SupplierMail {
  /** The `mailto:` URL, or null when there is nothing to send to. */
  href: string | null
  /** Turkish reason for the disabled button — null when it is enabled. */
  disabledReason: string | null
}

function supplierMail(data: PurchaseDetailResponse): SupplierMail {
  const email = data.header.supplierEmail
  if (email === null) {
    return { href: null, disabledReason: 'Cari kartında e-posta yok' }
  }
  if (!EMAIL_PATTERN.test(email)) {
    return {
      href: null,
      disabledReason: `Cari kartındaki e-posta adresi geçersiz: ${email}`,
    }
  }
  return { href: mailtoHref(email, data), disabledReason: null }
}

/**
 * A short Turkish summary the buyer can send as-is, or edit before sending.
 *
 * It deliberately does NOT restate the order line by line: that is what the
 * printed document is for, and a mail client's plain-text body is the wrong
 * place for a table. The body says so out loud, because a `mailto:` link
 * cannot attach anything — the PDF has to be attached by hand, and a summary
 * that quietly implied otherwise would be a lie the supplier acts on.
 *
 * CRLF, not LF: `%0D%0A` is what mail clients expect in a mailto body, and it
 * is what `encodeURIComponent` produces from '\r\n'.
 */
function mailtoHref(email: string, data: PurchaseDetailResponse): string {
  const { header, summary, lines } = data
  const unit = commonUnit(lines)
  const subject = `Satınalma Siparişi ${header.sipNo} - Modelle`
  const body = [
    `Sayın ${header.supplierName},`,
    '',
    `${header.sipNo} numaralı satınalma siparişimizin özeti:`,
    `- Sipariş tarihi: ${formatDate(header.orderDate)}`,
    `- Satır sayısı: ${formatQty(summary.lineCount)}`,
    `- Toplam miktar: ${formatQty(summary.orderedQty)}${unit ? ` ${unit}` : ''}`,
    ...(header.deliveryDate ? [`- Termin: ${formatDate(header.deliveryDate)}`] : []),
    '',
    'Siparişin satır bazındaki dökümü, ekteki PDF belgesinde yer almaktadır.',
    '',
    'İyi çalışmalar,',
    'Modelle',
  ].join('\r\n')

  /*
   * '@' stays literal — RFC 6068 allows it unencoded in the address and some
   * clients mishandle '%40'. Everything else is percent-encoded, which matters
   * for the non-ASCII addresses on file (e.g. "muhasebe@kazlıcesme.com.tr").
   */
  const address = encodeURIComponent(email).replace(/%40/g, '@')
  return `mailto:${address}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/** The unit shared by every line, or '' when the order mixes CIFT/DESI/ADET/MT. */
function commonUnit(lines: readonly PurchaseLine[]): string {
  const units = new Set(lines.map((line) => unitLabel(line.unit)).filter((u) => u !== ''))
  return units.size === 1 ? [...units][0]! : ''
}

/* ── Printable purchase order ─────────────────────────────────────────────── */

/**
 * Body class the print stylesheet keys off while the dialog is open — shared
 * by every document in `styles/print-docs.css`, never `print-statement` (that
 * one belongs to the cari ekstre and its own rules in styles.css).
 */
const PRINT_DOCUMENT_CLASS = 'print-document'

/**
 * The A4 purchase order — the document a supplier actually receives.
 *
 * The table shape follows the data. 448 of the 563 purchase orders in Korgun
 * carry no price at all, and most material lines carry no colour (`RKod 0` —
 * leather is bought by colour, soles and heels are not). Both column groups
 * are DROPPED when nothing on the order fills them, rather than printed as a
 * run of em dashes: on a document that leaves the building a column of dashes
 * reads as a printing fault, while a table without it reads as what it is — a
 * quantity order, priced by the standing agreement.
 *
 * Everything else is the same on every copy: the supplier block on the left,
 * the order's terms on the right, size runs under each material in the trade's
 * own `37:12 38:24` notation, and a signature area, because this is a document
 * that gets signed and filed on paper.
 */
function PurchaseOrderDocument({ data }: { data: PurchaseDetailResponse }) {
  const { header, summary, currencyTotals, lines } = data
  const priced = lines.some((line) => line.price > 0 || (line.netAmount ?? 0) !== 0)
  const colored = lines.some((line) => line.colors.length > 0)
  const pricedTotals = currencyTotals.filter((total) => total.netAmount !== 0)
  const unit = commonUnit(lines)
  const cityLine = [header.supplierCity, header.supplierCountry].filter(Boolean).join(' · ')

  const meta: { label: string; value: string; emphasis?: boolean }[] = [
    { label: 'Sipariş tarihi', value: formatDate(header.orderDate) },
    // Emphasised only when it exists: `TeslimTar` is set on 66 of 563 orders,
    // and boxing an em dash would give the empty case the loudest cell.
    {
      label: 'Teslim tarihi',
      value: formatDate(header.deliveryDate),
      emphasis: header.deliveryDate !== null,
    },
    { label: 'Depo', value: header.locationName ?? header.location ?? '—' },
    { label: 'Siparişi açan', value: header.orderedBy ?? header.createdBy ?? '—' },
    { label: 'Satır sayısı', value: formatQty(summary.lineCount) },
    {
      label: 'Toplam miktar',
      value: `${formatQty(summary.orderedQty)}${unit ? ` ${unit}` : ''}`,
    },
  ]
  if (header.documentNo) meta.push({ label: 'Belge no', value: header.documentNo })

  return (
    <PrintPortal className="print-doc">
      <header className="doc-head">
        <div>
          <p className="wordmark">Modelle</p>
          <p className="wordmark-sub">Ayakkabı fabrikası · Satınalma</p>
        </div>
        <div className="doc-id">
          <p className="doc-kind">Satınalma Siparişi</p>
          <p className="doc-no">{header.sipNo}</p>
          <p className="doc-date">{formatDate(header.orderDate)}</p>
        </div>
      </header>

      <section className="parties">
        <div className="party">
          <p className="doc-label">Tedarikçi</p>
          <p className="party-name">{header.supplierName}</p>
          <p className="party-line mono">{header.supplierKod}</p>
          {header.supplierTitle && header.supplierTitle !== header.supplierName ? (
            <p className="party-line">{header.supplierTitle}</p>
          ) : null}
          {header.supplierAddress ? <p className="party-line">{header.supplierAddress}</p> : null}
          {cityLine ? <p className="party-line">{cityLine}</p> : null}
          {header.supplierPhone ? (
            <p className="party-line">
              Tel <span className="mono">{header.supplierPhone}</span>
            </p>
          ) : null}
          {header.supplierEmail ? <p className="party-line">{header.supplierEmail}</p> : null}
        </div>

        <dl className="meta">
          {meta.map((field) => (
            <div key={field.label} className={field.emphasis ? 'meta-key' : undefined}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <table>
        <thead>
          <tr>
            <th className="col-no num">#</th>
            <th>Malzeme</th>
            {colored ? <th className="col-color">Renk</th> : null}
            <th className="col-qty num">Miktar</th>
            {priced ? <th className="col-price num">Birim fiyat</th> : null}
            {priced ? <th className="col-amount num">Tutar</th> : null}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const lineUnit = unitLabel(line.unit)
            const run = sizeRun(line.sizes)
            return (
              <tr key={line.sipHarInx}>
                <td className="num">{line.lineNo}</td>
                <td>
                  <span className="line-name">{line.materialName ?? line.skod ?? '—'}</span>
                  {line.skod ? <span className="line-code">{line.skod}</span> : null}
                  {run ? <span className="line-sizes">{run}</span> : null}
                  {line.lineNote ? <span className="line-note">{line.lineNote}</span> : null}
                </td>
                {colored ? (
                  <td>
                    {line.colors.length === 0
                      ? '—'
                      : line.colors.map((color) => (
                          <span key={color.rkod} className="line-color">
                            {color.colorName}
                            {line.colors.length > 1 ? ` · ${formatQty(color.orderedQty)}` : ''}
                          </span>
                        ))}
                  </td>
                ) : null}
                <td className="num">
                  {formatQty(line.orderedQty || line.quantity)}
                  {lineUnit ? ` ${lineUnit}` : ''}
                </td>
                {priced ? <td className="num">{formatPrice(line.price, line.currency)}</td> : null}
                {priced ? (
                  <td className="num">{formatAmountOrDash(line.netAmount, line.currency)}</td>
                ) : null}
              </tr>
            )
          })}
        </tbody>
      </table>

      {pricedTotals.length > 0 ? (
        <div className="totals">
          {pricedTotals.map((total) => {
            const main = total.currency === summary.currency
            return (
              <div
                key={total.currency}
                className={cn('total-row', main && 'total-main')}
              >
                <span>
                  {main ? `Sipariş toplamı (${total.currency})` : `${total.currency} karşılığı`}
                </span>
                <span className="total-value">{formatAmount(total.netAmount, total.currency)}</span>
              </div>
            )
          })}
        </div>
      ) : (
        <p className="totals-note">Bu sipariş fiyatsız verilmiştir; tutar bilgisi içermez.</p>
      )}

      {header.notes || header.closingNote ? (
        <section className="notes">
          {header.notes ? (
            <>
              <p className="doc-label">Sipariş notu</p>
              <p className="note-body">{header.notes}</p>
            </>
          ) : null}
          {header.closingNote ? (
            <>
              <p className="doc-label">Kapanış notu</p>
              <p className="note-body">{header.closingNote}</p>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="signatures">
        <div className="sign">
          <div className="sign-rule" />
          <p className="doc-label">Hazırlayan</p>
        </div>
        <div className="sign">
          <div className="sign-rule" />
          <p className="doc-label">Onaylayan</p>
        </div>
      </section>

      <footer className="doc-foot">
        <span>Panel — {printedOn()}</span>
        <span className="mono">Satınalma Siparişi {header.sipNo}</span>
      </footer>
    </PrintPortal>
  )
}

/**
 * A line's size run as `36:59 37:100 38:154` — the notation the shop floor and
 * the sole supplier both already use. The API leaves `sizes` empty on a
 * single-step line, so this is null exactly when there is no run to show.
 */
function sizeRun(sizes: readonly PurchaseSizeCell[]): string | null {
  const pairs = sizes
    .filter((cell) => cell.ordered > 0)
    .map((cell) => `${cell.size}:${formatQty(cell.ordered)}`)
  return pairs.length > 0 ? pairs.join(' ') : null
}

/* ── One material line ──────────────────────────────────────────────────────── */

function LineRow({ line }: { line: PurchaseLine }) {
  const unit = unitLabel(line.unit)

  return (
    <TableRow>
      <TableNumCell className="text-ink-muted">{line.lineNo}</TableNumCell>

      <TableCell>
        {/* A purchase line is nearly always material, and only 48 of the 628
            material cards carry a photo — so this is usually the sheet glyph,
            and the mamül lines are the ones that stand out with a picture. */}
        <span className="flex items-start gap-2">
          <ProductImage
            imageKey={line.imageKey}
            alt={`${line.materialName ?? line.skod ?? 'Malzeme'} fotoğrafı`}
            kind={line.stockKind === 'M' ? 'shoe' : 'material'}
            size="line"
          />
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium text-ink">
              {line.materialName ?? line.skod ?? '—'}
            </span>
            <span className="block truncate font-mono text-micro text-ink-muted">
              {line.skod ? <StockLink skod={line.skod} /> : '—'}
              {line.stockKind === 'M' ? ' · mamül' : ''}
              {line.closed ? ' · satır kapalı' : ''}
            </span>
            {line.lineNote ? (
              <span
                className="mt-0.5 block truncate text-micro text-ink-muted/80"
                title={`Satır notu: ${line.lineNote}`}
              >
                {line.lineNote}
              </span>
            ) : null}
          </span>
        </span>
      </TableCell>

      <TableCell className="hidden lg:table-cell">
        {line.colors.length === 0 ? (
          <span className="text-micro text-ink-muted/60">—</span>
        ) : (
          <span className="flex flex-col gap-1 leading-tight">
            {/* The colour's own photo where the material has one, else the
                card's — the same fallback the contract describes. */}
            {line.colors.map((color) => (
              <span key={color.rkod} className="flex items-center gap-1.5">
                <ProductImage
                  imageKey={color.imageKey ?? line.imageKey}
                  alt={`${line.materialName ?? line.skod ?? 'Malzeme'} · ${color.colorName} fotoğrafı`}
                  kind={line.stockKind === 'M' ? 'shoe' : 'material'}
                  size="line"
                />
                <span className="truncate text-data text-ink">{color.colorName}</span>
                <span className="shrink-0 font-mono text-micro tnum text-ink-muted">
                  {formatQty(color.receivedQty)}/{formatQty(color.orderedQty)}
                </span>
              </span>
            ))}
          </span>
        )}
      </TableCell>

      <TableCell className="hidden xl:table-cell">
        {line.sizes.length > 0 ? (
          <AsortiGrid cells={toSizeCells(line.sizes)} total density="sm" />
        ) : (
          <span className="text-micro text-ink-muted/60">Tek beden</span>
        )}
      </TableCell>

      <TableNumCell>
        {formatQty(line.orderedQty || line.quantity)}
        {unit ? <span className="text-micro text-ink-muted"> {unit}</span> : null}
      </TableNumCell>

      <TableNumCell className="text-ink-muted">{formatQty(line.receivedQty)}</TableNumCell>

      <TableNumCell
        className={cn(line.kalanQty > 0 ? 'font-medium text-ink' : 'text-ink-muted/60')}
      >
        {line.kalanQty > 0 ? formatQty(line.kalanQty) : '—'}
      </TableNumCell>

      <TableCell>
        <div className="flex flex-col gap-1">
          <ReceiptBar percent={line.percent} status={line.receiptStatus} showLabel={false} />
          <ReceiptBadge status={line.receiptStatus} />
        </div>
      </TableCell>

      <TableNumCell className="hidden text-ink-muted md:table-cell">
        {formatPrice(line.price, line.currency)}
      </TableNumCell>

      <TableNumCell className="font-medium">
        {formatAmountOrDash(line.netAmount, line.currency)}
      </TableNumCell>
    </TableRow>
  )
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Card>
        <div className="px-3.5 py-4">
          <span className="block h-2.5 w-24 animate-pulse rounded-xs bg-hairline" />
          <span className="mt-2 block h-6 w-64 animate-pulse rounded-xs bg-hairline" />
          <span className="mt-2 block h-2.5 w-44 animate-pulse rounded-xs bg-hairline" />
        </div>
      </Card>
      <Card>
        <TableScroller>
          <Table>
            <TableBody>
              <TableSkeleton rows={5} cols={8} />
            </TableBody>
          </Table>
        </TableScroller>
      </Card>
    </div>
  )
}
